// @flow
/** @jsx jsx */

import { jsx, css } from '@emotion/core';
import * as React from 'react';
import Button from 'react-bootstrap/lib/Button';
import Modal from 'react-bootstrap/lib/Modal';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import FormControl from 'react-bootstrap/lib/FormControl';
import { Mutation } from 'react-apollo';

import type { CommonActionProps } from './types.actions';

import ToggleController from '../../controllers/ToggleController';
import StatusController from '../../controllers/StatusController';
import ButtonAction from './ButtonAction';
import Row from '../Row';
import Col from '../Col';
import FormRow from '../FormRow';
import DescriptionCell from '../DescriptionCell';
import StatusMessage from '../StatusMessage';
import Prompt from '../Prompt';
import Loading from '../Loading';
import colors from '../../config/colors';
import { getNotesMutation } from '../../utils';
import messages from '../../config/messages';
import { DesktopView, MobileView, useMobileMediaQuery } from '../../config/mediaQuery';

const activeStatusStyles = {
  backgroundColor: colors.yellow,
  borderColor: colors.lightOrange,
  color: colors.black,
};

const ShowNotes = (props: {
  notes?: string,
  onNotesChange: (e: any) => mixed,
}) => {
  const isMobile = useMobileMediaQuery();
  return (
    <div>
      <FormRow
        css={{
          flexDirection: 'column',
        }}
      >
        {!isMobile &&
          <Col>
            <ControlLabel>Notes</ControlLabel>
          </Col>
        }
        <DescriptionCell>
          <FormControl
            componentClass="textarea"
            value={props.notes}
            css={{
              minHeight: '250px',
            }}
            onChange={props.onNotesChange}
          />
        </DescriptionCell>
      </FormRow>
    </div>
  );
}
type Props = {
  itemHasContent: boolean,
  onSave?: ({
    fieldName: string,
    value: string,
  }) => mixed,
  moduleName: string,
  notes?: string,
  fieldName: string,
  id: string,
};

type State = {
  notes: string,
  shouldCommitChanges: boolean,
  lastSavedNotes: string,
  id: string,
  dirty: boolean,
  notesChanged: boolean,
};

class NotesActions extends React.Component<CommonActionProps & Props, State> {
  static defaultProps = {
    size: 'xsmall',
    itemHasContent: false,
    onSave: () => { },
  };

  mutation: ?string;
  closingId: TimeoutID;

  constructor(props: CommonActionProps & Props) {
    super(props);

    this.state = {
      notes: props.notes || '',
      shouldCommitChanges: false,
      lastSavedNotes: '',
      id: '',
      dirty: false,
      notesChanged: false,
    };

    this.mutation = getNotesMutation(props.moduleName);
  }

  oldDerivedStateFromProps(
    nextProps: CommonActionProps & Props,
    prevState: State,
  ) {
    const { notes, id } = nextProps;

    if (notes !== prevState.notes || id !== prevState.id) {
      return this.setState({
        notes,
        id,
      });
    }

    return null;
  }

  componentDidMount() {
    this.oldDerivedStateFromProps(this.props, this.state)
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props !== prevProps) {
      this.oldDerivedStateFromProps(this.props, this.state);
    }
  }


  onNotesChange = (e: SyntheticKeyboardEvent<HTMLTextAreaElement>) => {
    this.setState({
      notes: e.currentTarget.value,
      notesChanged: true,
    });
  };

  onSaveCompleted = ({ onToggleVisibility }: any) => {
    this.setState(state => {
      return {
        shouldCommitChanges: true,
        lastSavedNotes: state.notes,
        dirty: false,
        notesChanged: false,
      };
    });

    if (this.closingId) {
      clearTimeout(this.closingId);
    }

    this.closingId = setTimeout(() => {
      this.onExit({
        onToggleVisibility,
        loading: false,
        dirty: false,
        notesChanged: false,
      });
    }, 2000);
  };

  commitChanges = (onToggleVisibility: () => mixed) => {
    // commit changes and update the collection. will cause the modal to close.
    if (typeof this.props.onSave === 'function') {
      this.props.onSave({
        fieldName: this.props.fieldName,
        value: this.state.lastSavedNotes,
      });
    }

    onToggleVisibility();
  };

  cancelConfirmPrompt = () => {
    this.setState({
      dirty: false,
      notesChanged: false,
    });
  };

  discardChanges = (onToggleVisibility: () => mixed) => {
    this.setState({
      notes: this.props.notes,
      dirty: false,
      notesChanged: false,
    });

    onToggleVisibility();
  };

  onExit = (options: {
    onToggleVisibility: () => mixed,
    loading: boolean,
    dirty: boolean,
  }) => {
    const { onToggleVisibility, loading, dirty } = options;

    // if the mutation hasn't finished this will avoid closing the modal until
    // the transaction has been completed.
    if (loading) return;

    if (dirty) {
      this.setState({
        dirty,
      });

      return;
    }

    if (this.state.shouldCommitChanges) {
      this.commitChanges(onToggleVisibility);
    } else {
      this.discardChanges(onToggleVisibility);
    }
  };

  componentWillUnmount() {
    if (this.closingId) {
      clearTimeout(this.closingId);
    }
  }

  render() {
    // show a disabled button in case there isn't a defined mutation for the action
    // or a proper field mapping for the notes field (each module defines
    // a different field name for Notes). Check src/config/fieldsModuleMapping.js
    // to see module-fields mapping.
    if (!this.mutation || !this.props.fieldName) {
      return (
        <ButtonAction bsSize={this.props.size} disabled>
          <Glyphicon glyph="list-alt" /> Notes
        </ButtonAction>
      );
    }

    const hasContent = this.props.itemHasContent || this.state.notes;

    return (
      <ToggleController>
        {({ onToggleVisibility, visible }) => (
          <Mutation
            mutation={this.mutation}
            variables={{
              input: {
                moduleName: this.props.moduleName,
                notes: this.state.notes,
                id: this.state.id,
              },
            }}
            onCompleted={dataMutation =>
              this.onSaveCompleted({ data: dataMutation, onToggleVisibility })
            }
          >
            {(mutation, { loading, error }) => (
              <StatusController hasError={!!error} loading={loading}>
                {({ status }) => (
                  <React.Fragment>
                    <DesktopView>
                      <ButtonAction
                        bsSize={this.props.size}
                        disabled={this.props.disabled}
                        onClick={onToggleVisibility}
                        css={[
                          hasContent && {
                            backgroundColor: colors.lightYellow,
                            borderColor: colors.yellow,
                            color: colors.black,
                            '&:hover': activeStatusStyles,
                            '&:active': activeStatusStyles,
                            '&:focus': activeStatusStyles,
                          },
                        ]}
                      >
                        <Glyphicon glyph="list-alt" />{' '}
                        <span
                          className={
                            hasContent &&
                            css`
                            font-weight: bold;
                          `
                          }
                        >
                          {(status === '' || status === 'error') && 'Notes'}
                          {status === 'loading' && 'Saving'}
                          {status === 'completed' && 'Saved!'}
                        </span>
                      </ButtonAction>
                    </DesktopView>
                    <MobileView>
                      <button onClick={onToggleVisibility} disabled={this.props.disabled}>
                        <img src='assets/notes.svg' alt='notes icon'/>
                        {(status === '' || status === 'error') && 'Notes'}
                        {status === 'loading' && 'Saving'}
                        {status === 'completed' && 'Saved!'}
                      </button>
                    </MobileView>
                    <MobileView>
                      {(isMobile) => (
                        <Modal
                          show={visible}
                          onHide={() =>
                            this.onExit({
                              onToggleVisibility,
                              loading,
                              dirty: this.state.notesChanged,
                            })
                          }
                          dialogClassName={isMobile ? 'accounts-notes-modal' : ''}
                        >
                          <Modal.Header closeButton={!isMobile}>
                            <Modal.Title>Notes</Modal.Title>
                          </Modal.Header>
                          <Modal.Body>
                            {loading && <Loading />}
                            {this.state.dirty && (
                              <Prompt
                                onCancel={this.cancelConfirmPrompt}
                                onConfirm={() =>
                                  this.onExit({
                                    onToggleVisibility,
                                    loading,
                                    dirty: false,
                                  })
                                }
                                message={messages.common.unsaved}
                              />
                            )}
                            <ShowNotes
                              onNotesChange={this.onNotesChange}
                              notes={this.state.notes}
                            />
                          </Modal.Body>
                          <Modal.Footer>
                            <DesktopView>
                              <Row
                                css={{
                                  alignItems: 'center',
                                }}
                              >
                                <Col>
                                  {(status === 'completed' || status === '') && (
                                    <StatusMessage status={status}>
                                      Notes successfully saved!
                                    </StatusMessage>
                                  )}
                                  {(status === 'error' || status === '') && (
                                    <StatusMessage status={status}>
                                      There has been an error while trying to save the
                                      notes.
                                    </StatusMessage>
                                  )}
                                </Col>
                                <Col
                                  css={{
                                    marginLeft: 'auto',
                                  }}
                                >
                                  <Button disabled={status !== ''} onClick={mutation}>
                                    {(status === '' || status === 'error') && 'Save'}
                                    {status === 'loading' && 'Saving...'}
                                    {status === 'completed' && 'Saved!'}
                                  </Button>
                                </Col>
                              </Row>
                            </DesktopView>
                            <MobileView>
                              <div className='columns-bottom-button-group'>
                                <button
                                  onClick={onToggleVisibility}
                                >
                                  Cancel
                                </button>
                                <button disabled={status !== ''} onClick={mutation}>
                                  {(status === '' || status === 'error') && 'Save'}
                                  {status === 'loading' && 'Saving...'}
                                  {status === 'completed' && 'Saved!'}
                                </button>
                              </div>
                            </MobileView>
                          </Modal.Footer>
                        </Modal>
                      )}
                    </MobileView>
                  </React.Fragment>
                )}
              </StatusController>
            )}
          </Mutation>
        )}
      </ToggleController>
    );
  }
}

export default NotesActions;
